import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import './firebase'; // Import to initialize Firebase and Analytics
import { apiClient } from './api/client';
import { AuthPage } from './components/AuthPage';
import { DisclaimerPage } from './components/DisclaimerPage';
import { MapPage } from './components/MapPage';
import { SearchOverlay } from './components/SearchOverlay';
import AppUpdateNotification from './components/AppUpdateNotification';
import { FavouritesOverlay } from './components/FavouritesOverlay';
import { SpotOverlay } from './components/SpotOverlay';
import { PointOverlay } from './components/PointOverlay';
import { UserAccountOverlay } from './components/UserAccountOverlay';
import { AboutOverlay } from './components/AboutOverlay';
import { LogoutConfirmationOverlay } from './components/LogoutConfirmationOverlay';
import { AnnouncementOverlay } from './components/AnnouncementOverlay';
import { SessionGuard } from './components/SessionGuard';
import { MapProvider } from './contexts/MapContext';
import { I18nProvider, useI18n } from './i18n/I18nContext';
import { Router } from './router/Router';
import { RouterProvider, useRoute } from './router/RouterContext';
import { routes } from './router/routes';
import { storageService } from './services/StorageService';
import { announcementService, Announcement } from './services/AnnouncementService';

const NOVITA_ID = 'novita-3.5.0';

// This component now receives its state as props from the main App component.
function AppContent({ showUpdate, onUpdate }: { showUpdate: boolean, onUpdate: () => void }) {
  const [isAuthenticated, setIsAuthenticated] = useState(apiClient.isAuthenticated());
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(storageService.isDisclaimerAccepted());
  const [currentAnnouncement, setCurrentAnnouncement] = useState<Announcement | null>(null);
  const { t } = useI18n();
  const route = useRoute();

  // Check for announcements when the app loads and user is authenticated with disclaimer accepted
  useEffect(() => {
    if (isAuthenticated && disclaimerAccepted) {
      const checkAnnouncements = async () => {
        const announcement = await announcementService.getNextAnnouncement();
        if (announcement) {
          setCurrentAnnouncement(announcement);
        } else if (!storageService.isAnnouncementDismissed(NOVITA_ID)) {
          // le novita' della versione, una volta sola per dispositivo; scritte qui e non in
          // Firestore, che e' in comune con le versioni di prova
          setCurrentAnnouncement({
            id: NOVITA_ID, title: t('news.title') as string, message: t('news.message') as string,
            createdAt: new Date(), priority: 'low', active: true,
          });
        }
      };
      checkAnnouncements();
    }
  }, [isAuthenticated, disclaimerAccepted, t]);

  const handleAnnouncementDismiss = () => {
    if (currentAnnouncement) {
      announcementService.dismissAnnouncement(currentAnnouncement.id);
      setCurrentAnnouncement(null);
    }
  };

  const handleLogout = () => {
    apiClient.logout();
    storageService.clearDisclaimerAccepted();
    setIsAuthenticated(false);
    setDisclaimerAccepted(false);
  };

  const handleDisclaimerAccept = () => {
    storageService.setDisclaimerAccepted();
    setDisclaimerAccepted(true);
  };

  const handleDisclaimerReject = () => {
    apiClient.logout();
    storageService.clearDisclaimerAccepted();
    setIsAuthenticated(false);
    setDisclaimerAccepted(false);
  };

  if (!isAuthenticated) {
    return <AuthPage onAuthSuccess={() => setIsAuthenticated(true)} />;
  }

  if (!disclaimerAccepted) {
    return <DisclaimerPage onAccept={handleDisclaimerAccept} onReject={handleDisclaimerReject} />;
  }

  // Render based on current route
  return (
    <div className="app">
      <MapProvider onLogout={handleLogout}>
        <MapPage onLogout={handleLogout} />

        {/* Render overlays based on route name */}
        {route?.name === 'search' && <SearchOverlay />}
        {route?.name === 'favourites' && <FavouritesOverlay />}
        {route?.name === 'user' && <UserAccountOverlay />}
        {route?.name === 'about' && <AboutOverlay />}
        {route?.name === 'logout' && <LogoutConfirmationOverlay onLogout={handleLogout} />}

        {/* Spot overlay (can be accessed from multiple contexts) */}
        {(route?.name === 'spot' || route?.name === 'search-spot' || route?.name === 'favourites-spot') && (
          <SpotOverlay windgramId={route.params.id} />
        )}

        {/* Windgram di un punto qualunque (pressione lunga sulla mappa) */}
        {route?.name === 'point' && (
          <PointOverlay lat={parseFloat(route.params.lat)} lon={parseFloat(route.params.lon)} />
        )}

        {/* una sola sessione attiva per tipo di dispositivo (SessionGuard) */}
        <SessionGuard />

        {/* Announcement overlay (shown when there's an active announcement) */}
        {currentAnnouncement && (
          <AnnouncementOverlay
            announcement={currentAnnouncement}
            onDismiss={handleAnnouncementDismiss}
          />
        )}
      </MapProvider>
      <AppUpdateNotification show={showUpdate} onUpdate={onUpdate} />
    </div>
  );
}

function App() {
  const router = useMemo(() => new Router(routes), []);

  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    console.log('APP: Update listener useEffect running.');
    const handleSwUpdate = (event: Event) => {
      console.log('APP: swUpdate event handled.');
      const registration = (event as CustomEvent<ServiceWorkerRegistration>).detail;
      setWaitingWorker(registration);
      setShowUpdate(true);
    };

    // Listen for the custom event indicating a new service worker is waiting.
    document.addEventListener('swUpdate', handleSwUpdate);

    // Check if a waiting service worker already exists on component mount.
    // This handles the race condition.
    if (window.waitingServiceWorker) {
      console.log('APP: Found waiting SW on mount.');
      setWaitingWorker(window.waitingServiceWorker);
      setShowUpdate(true);
    }

    return () => {
      document.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);

  useEffect(() => {
    console.log(`APP: showUpdate state changed to: ${showUpdate}`);
  }, [showUpdate]);

  useEffect(() => {
    // This effect handles the page reload after the new service worker has taken control.
    const handleControllerChange = () => {
      console.log('APP: Controller changed. Reloading page.');
      // Add a small delay to ensure the new service worker is fully active.
      setTimeout(() => {
        window.location.reload();
      }, 100);
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  useEffect(() => {
    // Check version on mount and force reload if cache is stale
    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });
        const data = await response.json();
        const cachedVersion = localStorage.getItem('app-version');
        
        console.log('APP: Version check:', {
          server: data.version,
          cached: cachedVersion,
          match: cachedVersion === data.version
        });
        
        // Always clear caches if version doesn't match OR if no cached version in TWA
        const isTWA = window.matchMedia('(display-mode: standalone)').matches;
        const needsUpdate = cachedVersion !== data.version;
        
        if (needsUpdate) {
          console.log('APP: Version mismatch detected. Clearing caches and reloading...');
          // Clear all caches
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            console.log('APP: Clearing caches:', cacheNames);
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          }
          // Unregister service worker
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            console.log('APP: Unregistering service workers:', registrations.length);
            await Promise.all(registrations.map(reg => reg.unregister()));
          }
          localStorage.setItem('app-version', data.version);
          // Force reload without cache
          console.log('APP: Forcing reload...');
          window.location.href = window.location.href.split('?')[0] + '?v=' + data.version + '&t=' + Date.now();
        } else if (!cachedVersion) {
          // First visit - just save the version
          console.log('APP: First visit, saving version:', data.version);
          localStorage.setItem('app-version', data.version);
        } else {
          console.log('APP: Version up to date');
        }
      } catch (error) {
        console.error('APP: Error checking version:', error);
      }
    };

    checkVersion();

    // Periodically check for updates in the background.
    // Only in production.
    if (process.env.NODE_ENV === 'production') {
      const interval = setInterval(async () => {
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            console.log('APP: Periodically checking for SW update...');
            await registration.update();
          } catch (error) {
            console.error('APP: Error during periodic SW update check:', error);
          }
        }
      }, 1000 * 60 * 30); // Every 30 minutes

      return () => clearInterval(interval);
    }
  }, []);


  const handleUpdate = () => {
    // Tell the waiting service worker to take over.
    if (waitingWorker && waitingWorker.waiting) {
      console.log('APP: Sending SKIP_WAITING message.');
      waitingWorker.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    // Hide the notification. The 'controllerchange' event will handle the reload.
    setShowUpdate(false);
  };

  return (
    <I18nProvider>
      <RouterProvider router={router}>
        <AppContent showUpdate={showUpdate} onUpdate={handleUpdate} />
      </RouterProvider>
    </I18nProvider>
  );
}

export default App;